import Constants from '@/Constants';
import { ProgramClass } from '@/models/ProgramClass';
import http from '@/services/HttpService';
import { call, put } from 'redux-saga/effects';
import { PayloadAction } from 'typesafe-actions';
import {
    createProgramClassFailure,
    createProgramClassSuccess,
    deleteProgramClassFailure,
    deleteProgramClassSuccess,
    enrollStudentFailure,
    enrollStudentSuccess,
    getEnrolledStudentsFailure,
    getEnrolledStudentsSuccess,
    getEnrollmentByStudentFailure,
    getEnrollmentByStudentSuccess,
    getProgramClassFailure,
    getProgramClassSuccess,
    getProgramClassToUpdateEnrollmentsCountSuccess,
    getStudentsWithEnrollInfoFailure,
    getStudentsWithEnrollInfoSuccess,
    massImportEnrollmentFailure,
    massImportEnrollmentSuccess,
    unenrollStudentFailure,
    unenrollStudentSuccess,
    updateProgramClassFailure,
    updateProgramClassSuccess,
} from './actions';
import { ClassEnrollment, EnrollmentProcessData, ProgramClassTypes, StudentsRequestData } from './types';

export function* createProgramClassForCourseRequest(action: PayloadAction<ProgramClassTypes.CREATE_PROGRAM_CLASS_FOR_COURSE_REQUEST, ProgramClass>) {
    try {
        const { courseId } = action.payload;
        const { data } = yield call(
            http.post,
            `${Constants.FLOWLMS_PROGRAM_CLASS_FOR_COURSE_URI.replace('%c', courseId.toString())}`,
            action.payload,
        );
        yield put(createProgramClassSuccess(data));
    } catch (err) {
        yield put(createProgramClassFailure(err));
    }
}
export function* createProgramClassForProgramRequest(
    action: PayloadAction<ProgramClassTypes.CREATE_PROGRAM_CLASS_FOR_PROGRAM_REQUEST, ProgramClass>,
) {
    try {
        const { programId } = action.payload;
        const { data } = yield call(
            http.post,
            `${Constants.FLOWLMS_PROGRAM_CLASS_FOR_PROGRAM_URI.replace('%c', programId.toString())}`,
            action.payload,
        );
        yield put(createProgramClassSuccess(data));
    } catch (err) {
        yield put(createProgramClassFailure(err));
    }
}

export function* updateProgramClass(action: PayloadAction<ProgramClassTypes.UPDATE_PROGRAM_CLASS_REQUEST, ProgramClass>) {
    try {
        const { id } = action.payload;
        yield call(http.put, `${Constants.LMS_PROGRAM_CLASS_URI}/${id}`, action.payload);
        yield put(updateProgramClassSuccess(action.payload));
    } catch (err) {
        yield put(updateProgramClassFailure(err));
    }
}

export function* deleteProgramClass(action: PayloadAction<ProgramClassTypes.DELETE_PROGRAM_CLASS_REQUEST, number>) {
    try {
        yield call(http.delete, `${Constants.LMS_PROGRAM_CLASS_URI}/${action.payload}`);
        yield put(deleteProgramClassSuccess());
    } catch (err) {
        yield put(deleteProgramClassFailure(err));
    }
}

export function* getProgramClass(
    action: PayloadAction<ProgramClassTypes.GET_PROGRAM_CLASS_REQUEST, { id: number; onlyUpdateEnrollmentCount: boolean }>,
) {
    try {
        const { id, onlyUpdateEnrollmentCount } = action.payload;
        const { data } = yield call(http.get, `${Constants.LMS_PROGRAM_CLASS_URI}/${id}?withEnrollmentsCount=true`);

        if (onlyUpdateEnrollmentCount) {
            yield put(getProgramClassToUpdateEnrollmentsCountSuccess(data));
        } else {
            yield put(getProgramClassSuccess(data));
        }
    } catch (err) {
        yield put(getProgramClassFailure(err));
    }
}

export function* getEnrolledStudents(action: PayloadAction<ProgramClassTypes.GET_ENROLLED_STUDENTS_REQUEST, StudentsRequestData>) {
    try {
        const { offset, limit, programClassId, search } = action.payload;
        let params = `offset=${offset}&limit=${limit}&programClassId=${programClassId}&getPersonData=true`;

        if (search) {
            params += `&search=${search}`;
        }

        const { data } = yield call(http.get, `${Constants.LMS_CLASS_ENTOLLMENTS_URI}?${params}`);
        yield put(getEnrolledStudentsSuccess(data));
    } catch (err) {
        yield put(getEnrolledStudentsFailure(err));
    }
}

export function* getEnrollmentByStudent(action: PayloadAction<ProgramClassTypes.GET_ENROLLMENT_BY_STUDENT_REQUEST, ClassEnrollment>) {
    try {
        const params = `personIds=${action.payload.personId}&programClassId=${action.payload.programClass.id}`;
        const { data } = yield call(http.get, `${Constants.LMS_CLASS_ENTOLLMENTS_URI}?${params}`);
        yield put(getEnrollmentByStudentSuccess(data));
    } catch (err) {
        yield put(getEnrollmentByStudentFailure(err));
    }
}

export function* enroll(action: PayloadAction<ProgramClassTypes.ENROLL_STUDENT_REQUEST, ClassEnrollment>) {
    try {
        const { data } = yield call(http.post, `${Constants.LMS_CLASS_ENTOLLMENTS_URI}`, action.payload);
        yield put(enrollStudentSuccess(data));
    } catch (err) {
        yield put(enrollStudentFailure(err));
    }
}

export function* unenroll(action: PayloadAction<ProgramClassTypes.UNENROLL_STUDENT_REQUEST, number>) {
    try {
        yield call(http.delete, `${Constants.LMS_CLASS_ENTOLLMENTS_URI}/${action.payload}`);
        yield put(unenrollStudentSuccess(action.payload));
    } catch (err) {
        yield put(unenrollStudentFailure(err));
    }
}

export function* getStudentsWithEnrollInfo(action: PayloadAction<ProgramClassTypes.GET_STUDENTS_WITH_ENROLL_INFO_REQUEST, StudentsRequestData>) {
    try {
        const { offset, limit, programClassId, search, onlyActive, orderBy } = action.payload;
        let params = `offset=${offset}&limit=${limit}&onlyActive=${onlyActive}`;

        if (search) {
            params += `&search=${search}`;
        }

        if (orderBy) {
            params += `&orderBy=${orderBy}`;
        }

        const { data } = yield call(http.get, `${Constants.LMS_PROGRAM_CLASS_ENROLL_INFO_URI.replace('%p', programClassId.toString())}?${params}`);

        yield put(getStudentsWithEnrollInfoSuccess(data));
    } catch (err) {
        yield put(getStudentsWithEnrollInfoFailure(err));
    }
}

export function* massImportEnrollment(action: PayloadAction<ProgramClassTypes.MASS_IMPORT_ENROLLMENT_REQUEST, EnrollmentProcessData>) {
    try {
        const { programClassId } = action.payload;
        yield call(http.post, `${Constants.LMS_PROGRAM_CLASS_MASS_IMPORT_URI.replace('%p', String(programClassId))}`, action.payload);

        yield put(massImportEnrollmentSuccess());
    } catch (err) {
        yield put(massImportEnrollmentFailure(err));
    }
}
